import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatINR, formatDate } from "@/lib/api";
import { STATUSES, STATUS_COLORS, slug } from "@/lib/constants";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Loader2, Search, FileText, Webhook } from "lucide-react";

const KanbanCard = ({ app, onDragStart, onOpen }) => (
  <div draggable onDragStart={(e) => onDragStart(e, app.id)} onClick={() => onOpen(app.id)} data-testid={`kanban-card-${app.id}`}
    className="cursor-grab active:cursor-grabbing bg-white text-[#0F172A] rounded-xl p-4 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all border-l-4" style={{ borderLeftColor: STATUS_COLORS[app.status] }}>
    <div className="flex items-start justify-between gap-2">
      <p className="font-semibold text-sm truncate" data-testid="kanban-card-client">{app.client?.name || "Unknown client"}</p>
      <span className="text-[10px] uppercase tracking-wider text-slate-500 shrink-0">{app.loan_type}</span>
    </div>
    <p className="mt-1 font-display text-lg font-bold">{formatINR(app.requested_amount)}</p>
    {app.lender_name && <p className="text-xs text-slate-600 truncate" data-testid="kanban-card-lender">{app.lender_name}</p>}
    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
      <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {app.documents.length} docs</span>
      <span>{formatDate(app.created_at)}</span>
    </div>
  </div>
);

export default function AdminPipeline() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [over, setOver] = useState(null);

  const load = async () => {
    try {
      const [a, s, e] = await Promise.all([api.get("/applications"), api.get("/admin/stats"), api.get("/admin/webhook-events")]);
      setApps(a.data); setStats(s.data); setEvents(e.data);
    } catch (err) {
      toast.error(formatApiError(err)); setApps([]);
    }
  };
  useEffect(() => { load(); }, []);

  const onDrop = async (e, status) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const app = apps.find((x) => x.id === id);
    if (!app || app.status === status) return;
    const prev = apps;
    setApps(apps.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/applications/${id}/status`, { status });
      toast.success(`${app.client?.name || "Application"} moved to ${status} · webhook fired`);
      const [s, ev] = await Promise.all([api.get("/admin/stats"), api.get("/admin/webhook-events")]);
      setStats(s.data); setEvents(ev.data);
    } catch (err) {
      setApps(prev);
      toast.error(formatApiError(err));
    }
  };

  const filtered = (apps || []).filter((a) => {
    const t = q.toLowerCase();
    return !t || a.client?.name?.toLowerCase().includes(t) || a.loan_type.toLowerCase().includes(t) || String(a.requested_amount).includes(t) || a.id.includes(t);
  });

  return (
    <div className="min-h-screen bg-[#0B132B] text-white">
      <Navbar />
      <main className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="admin-pipeline">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-[#FF6B00]">Falcon Operations</p>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">Pipeline</h1>
            {stats && (
              <p className="mt-2 text-slate-400" data-testid="pipeline-stats">
                {stats.total} files · {formatINR(stats.pipeline_value)} in pipeline · Webhook {stats.webhook_url_configured ? "configured" : "not configured (payloads logged)"}
              </p>
            )}
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, loan type, amount…" className="pl-9 h-11 bg-white text-[#0F172A]" data-testid="pipeline-search-input" />
          </div>
        </div>

        {apps === null ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 text-[#FF6B00] animate-spin" /></div>
        ) : (
          <div className="mt-8 flex gap-4 overflow-x-auto pb-4 kanban-scroll">
            {STATUSES.map((s) => {
              const col = filtered.filter((a) => a.status === s);
              return (
                <section key={s} data-testid={`kanban-column-${slug(s)}`}
                  onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver(null)} onDrop={(e) => onDrop(e, s)}
                  className={`min-w-[260px] w-[260px] flex-shrink-0 rounded-2xl p-3 transition-colors ${over === s ? "bg-orange-500/15 ring-2 ring-[#FF6B00]" : "bg-white/5"}`}>
                  <header className="flex items-center justify-between px-1 pb-3">
                    <span className="flex items-center gap-2 text-sm font-semibold"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLORS[s] }} /> {s}</span>
                    <span className="text-xs font-semibold bg-white/10 rounded-full px-2 py-0.5" data-testid={`kanban-count-${slug(s)}`}>{col.length}</span>
                  </header>
                  <div className="space-y-3 min-h-[120px]">
                    {col.map((a) => (
                      <KanbanCard key={a.id} app={a} onDragStart={(e, id) => e.dataTransfer.setData("text/plain", id)} onOpen={(id) => navigate(`/admin/applications/${id}`)} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <section className="mt-10 navy-glass rounded-2xl p-6" data-testid="webhook-events">
          <h2 className="flex items-center gap-2 font-display text-base md:text-lg font-semibold"><Webhook className="h-5 w-5 text-[#FF6B00]" /> Recent status webhooks</h2>
          {events.length === 0 ? (
            <p className="mt-3 text-sm text-slate-400">No status changes yet. Drag a card to a new column to fire the first webhook.</p>
          ) : (
            <ul className="mt-3 divide-y divide-white/10 text-sm">
              {events.slice(0, 8).map((ev) => (
                <li key={ev.id} className="py-2 flex flex-wrap justify-between gap-2" data-testid={`webhook-event-${ev.id}`}>
                  <span><strong>{ev.payload.client_name}</strong> ({ev.payload.phone_number}) → <span className="text-[#FF6B00]">{ev.payload.new_status}</span></span>
                  <span className="text-slate-400">{ev.delivered ? `Delivered (${ev.response_code})` : ev.note || ev.error || "Not delivered"} · {new Date(ev.created_at).toLocaleTimeString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
